import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Shield, Users, LayoutDashboard, Lock, Save, RefreshCcw } from "lucide-react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import RoleAccessMatrix from "../components/RoleAccessMatrix";
import DeletedRoles from "../components/DeletedRoles";

const CARD_STYLE = {
  borderColor: "var(--c-border)",
  background: "var(--c-card)",
};

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-flat p-4 flex items-center gap-3" style={CARD_STYLE}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--c-accent-soft)" }}>
        <Icon size={18} strokeWidth={1.7} style={{ color: "var(--c-accent)" }} />
      </div>
      <div>
        <div className="text-[12px]" style={{ color: "var(--c-text-muted)" }}>{label}</div>
        <div className="font-heading text-[24px] font-semibold">{value ?? "—"}</div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer mr-3 mb-2">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function ClientUserRow({ user, dashboards, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    assigned_dashboard_id: user.assigned_dashboard_id || "",
    allowed_consignee_ids: (user.allowed_consignee_ids || []).join(", "),
    is_active: user.is_active !== false,
    ai_access: !!user.ai_access,
    builder_access: !!user.builder_access,
    datasource_access: !!user.datasource_access,
    settings_access: !!user.settings_access,
  }));
  const [saving, setSaving] = useState(false);

  async function save() {
    try {
      setSaving(true);
      const ids = String(draft.allowed_consignee_ids || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x));

      await api.patch(`/access-management/users/${user.id}`, {
        assigned_dashboard_id: draft.assigned_dashboard_id,
        allowed_dashboard_ids: draft.assigned_dashboard_id ? [draft.assigned_dashboard_id] : [],
        allowed_consignee_ids: ids,
        client_data_filters: {
          "ds-camaroo-qa-sales": [
            { field: "ConsigneeID", op: "in", value: ids }
          ]
        },
        is_active: draft.is_active,
        ai_access: draft.ai_access,
        builder_access: draft.builder_access,
        datasource_access: draft.datasource_access,
        settings_access: draft.settings_access,
      });

      toast.success("Client access updated");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr>
      <td className="align-top">
        <div className="font-medium">{user.name}</div>
        <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{user.email}</div>
      </td>
      <td className="align-top">
        {editing ? (
          <select
            className="input min-w-[260px]"
            value={draft.assigned_dashboard_id}
            onChange={(e) => setDraft((d) => ({ ...d, assigned_dashboard_id: e.target.value }))}
          >
            <option value="">Select dashboard</option>
            {dashboards.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        ) : (
          <div className="text-[12px]">{user.assigned_dashboard_id}</div>
        )}
      </td>
      <td className="align-top">
        {editing ? (
          <input
            className="input min-w-[170px]"
            value={draft.allowed_consignee_ids}
            onChange={(e) => setDraft((d) => ({ ...d, allowed_consignee_ids: e.target.value }))}
            placeholder="8, 71"
          />
        ) : (
          <div className="text-[12px]">{(user.allowed_consignee_ids || []).join(", ") || "—"}</div>
        )}
      </td>
      <td className="align-top">
        {editing ? (
          <div className="min-w-[260px]">
            <Toggle label="Active" checked={draft.is_active} onChange={(v) => setDraft((d) => ({ ...d, is_active: v }))} />
            <Toggle label="Ask AI" checked={draft.ai_access} onChange={(v) => setDraft((d) => ({ ...d, ai_access: v }))} />
            <Toggle label="Builder" checked={draft.builder_access} onChange={(v) => setDraft((d) => ({ ...d, builder_access: v }))} />
            <Toggle label="Data Sources" checked={draft.datasource_access} onChange={(v) => setDraft((d) => ({ ...d, datasource_access: v }))} />
            <Toggle label="Settings" checked={draft.settings_access} onChange={(v) => setDraft((d) => ({ ...d, settings_access: v }))} />
          </div>
        ) : (
          <div className="text-[12px] leading-5">
            <div>{user.is_active === false ? "Inactive" : "Active"}</div>
            <div style={{ color: "var(--c-text-muted)" }}>
              AI: {user.ai_access ? "Yes" : "No"} · Builder: {user.builder_access ? "Yes" : "No"}
            </div>
          </div>
        )}
      </td>
      <td className="align-top text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button className="btn btn-outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              <Save size={14} /> Save
            </button>
          </div>
        ) : (
          <button className="btn btn-outline" onClick={() => setEditing(true)}>Edit</button>
        )}
      </td>
    </tr>
  );
}

function RoleUserRow({ user, dashboards, accessRoles, canAssignRole, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    access_role_id: user.access_role_id || "",
    assigned_dashboard_id: user.assigned_dashboard_id || "",
    is_active: user.is_active !== false,
    ai_access: !!user.ai_access,
    builder_access: !!user.builder_access,
    datasource_access: !!user.datasource_access,
    settings_access: !!user.settings_access,
  }));
  const [saving, setSaving] = useState(false);

  async function save() {
    try {
      setSaving(true);
      const allowed = draft.assigned_dashboard_id ? [draft.assigned_dashboard_id] : [];
      await api.patch(`/access-management/users/${user.id}`, {
        assigned_dashboard_id: draft.assigned_dashboard_id,
        allowed_dashboard_ids: allowed,
        is_active: draft.is_active,
        ai_access: draft.ai_access,
        builder_access: draft.builder_access,
        datasource_access: draft.datasource_access,
        settings_access: draft.settings_access,
        ...(canAssignRole && draft.access_role_id !== (user.access_role_id || "") ? { access_role_id: draft.access_role_id } : {}),
      });

      toast.success("Role access updated");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr>
      <td>
        <div className="font-medium">{user.name}</div>
        <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{user.email}</div>
      </td>
      <td>{user.role}</td>
      <td>
        {editing && canAssignRole && user.role !== "super_admin" ? (
          <select
            className="input min-w-[200px]"
            value={draft.access_role_id}
            onChange={(e) => setDraft((d) => ({ ...d, access_role_id: e.target.value }))}
          >
            <option value="">No access role (full access by system role)</option>
            {accessRoles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        ) : (
          <span className="text-[12px]">
            {user.role === "super_admin" ? "All screens" : (accessRoles.find((r) => r.id === user.access_role_id)?.name || "—")}
          </span>
        )}
      </td>
      <td>
        {editing ? (
          <select
            className="input min-w-[260px]"
            value={draft.assigned_dashboard_id}
            onChange={(e) => setDraft((d) => ({ ...d, assigned_dashboard_id: e.target.value }))}
          >
            <option value="">No locked dashboard</option>
            {dashboards.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        ) : (
          <span className="text-[12px]">{user.assigned_dashboard_id || "Full / normal access"}</span>
        )}
      </td>
      <td>
        {editing ? (
          <div className="min-w-[260px]">
            <Toggle label="Active" checked={draft.is_active} onChange={(v) => setDraft((d) => ({ ...d, is_active: v }))} />
            <Toggle label="Ask AI" checked={draft.ai_access} onChange={(v) => setDraft((d) => ({ ...d, ai_access: v }))} />
            <Toggle label="Builder" checked={draft.builder_access} onChange={(v) => setDraft((d) => ({ ...d, builder_access: v }))} />
            <Toggle label="Data Sources" checked={draft.datasource_access} onChange={(v) => setDraft((d) => ({ ...d, datasource_access: v }))} />
            <Toggle label="Settings" checked={draft.settings_access} onChange={(v) => setDraft((d) => ({ ...d, settings_access: v }))} />
          </div>
        ) : (
          <span className="text-[12px]">{user.is_active === false ? "Inactive" : "Active"}</span>
        )}
      </td>
      <td className="text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button className="btn btn-outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              <Save size={14} /> Save
            </button>
          </div>
        ) : (
          <button className="btn btn-outline" onClick={() => setEditing(true)}>Edit</button>
        )}
      </td>
    </tr>
  );
}


function CreateRoleUserPanel({ accessRoles, onCreated }) {
  const empty = { name: "", email: "", password: "", access_role_id: "", role: "manager" };
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    try {
      setSaving(true);
      await api.post("/access-management/users", { ...form, email: form.email.trim().toLowerCase(), name: form.name.trim() });
      toast.success("User created");
      setForm(empty);
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card-flat p-4" style={CARD_STYLE}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="font-heading text-[16px] font-semibold">Create ERP Role User</div>
          <div className="text-[12px]" style={{ color: "var(--c-text-muted)" }}>
            Create an analytics login and assign an ERP access role. The user sees only that role's screens.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setOpen((v) => !v)}>{open ? "Close" : "Create User"}</button>
      </div>
      {open && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
          <div>
            <label className="text-[12px] font-medium">Name</label>
            <input className="input mt-1" value={form.name} onChange={set("name")} required />
          </div>
          <div>
            <label className="text-[12px] font-medium">Email</label>
            <input className="input mt-1" type="email" value={form.email} onChange={set("email")} required />
          </div>
          <div>
            <label className="text-[12px] font-medium">Password (min 8)</label>
            <input className="input mt-1" type="password" minLength={8} value={form.password} onChange={set("password")} required autoComplete="new-password" />
          </div>
          <div>
            <label className="text-[12px] font-medium">Access Role</label>
            <select className="input mt-1" value={form.access_role_id} onChange={set("access_role_id")} required>
              <option value="">Select role</option>
              {accessRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-medium">Data permission level</label>
            <select className="input mt-1" value={form.role} onChange={set("role")}>
              <option value="viewer">Viewer (read only)</option>
              <option value="manager">Manager (read, export)</option>
              <option value="analyst">Analyst (build dashboards, SQL)</option>
            </select>
          </div>
          <div className="md:col-span-2 lg:col-span-5 flex justify-end">
            <button className="btn btn-primary" disabled={saving} type="submit"><Save size={14} /> {saving ? "Creating..." : "Create User"}</button>
          </div>
        </form>
      )}
    </div>
  );
}


function CreateClientPanel({ dashboards, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    assigned_dashboard_id: "",
    allowed_consignee_ids: "",
    password: "Camaroo@123",
  });

  async function submit(e) {
    e.preventDefault();

    const ids = String(form.allowed_consignee_ids || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x));

    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }

    if (!form.email.trim() || !form.email.includes("@")) {
      toast.error("Valid client email is required");
      return;
    }

    if (!form.assigned_dashboard_id) {
      toast.error("Assigned dashboard is required");
      return;
    }

    if (!ids.length) {
      toast.error("At least one ConsigneeID is required");
      return;
    }

    try {
      setSaving(true);
      await api.post("/access-management/client-users", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        assigned_dashboard_id: form.assigned_dashboard_id,
        allowed_consignee_ids: ids,
        password: form.password || "Camaroo@123",
      });

      toast.success("Client user created");
      setForm({
        name: "",
        email: "",
        assigned_dashboard_id: "",
        allowed_consignee_ids: "",
        password: "Camaroo@123",
      });
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  }

  const clientDashboards = dashboards.filter((d) => (d.tags || []).includes("client-dashboard"));

  return (
    <div className="card-flat p-4" style={CARD_STYLE}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="font-heading text-[16px] font-semibold">Create New Client Access</div>
          <div className="text-[12px]" style={{ color: "var(--c-text-muted)" }}>
            Create a client login, assign dashboard, and apply ConsigneeID data filter.
          </div>
        </div>

        <button className="btn btn-primary" onClick={() => setOpen((v) => !v)}>
          {open ? "Close" : "Create Client"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
          <div>
            <label className="text-[12px] font-medium">Client Name</label>
            <input
              className="input mt-1"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Client Company Name"
            />
          </div>

          <div>
            <label className="text-[12px] font-medium">Client Email</label>
            <input
              className="input mt-1"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="client@camaroo.io"
            />
          </div>

          <div>
            <label className="text-[12px] font-medium">Dashboard</label>
            <select
              className="input mt-1"
              value={form.assigned_dashboard_id}
              onChange={(e) => setForm((f) => ({ ...f, assigned_dashboard_id: e.target.value }))}
            >
              <option value="">Select dashboard</option>
              {clientDashboards.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-medium">ConsigneeIDs</label>
            <input
              className="input mt-1"
              value={form.allowed_consignee_ids}
              onChange={(e) => setForm((f) => ({ ...f, allowed_consignee_ids: e.target.value }))}
              placeholder="8, 71"
            />
          </div>

          <div>
            <label className="text-[12px] font-medium">Password</label>
            <input
              className="input mt-1"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="Camaroo@123"
            />
          </div>

          <div className="md:col-span-2 lg:col-span-5 flex justify-end">
            <button className="btn btn-primary" disabled={saving} type="submit">
              <Save size={14} /> {saving ? "Creating..." : "Create Client Access"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


export default function AccessManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [clientUsers, setClientUsers] = useState([]);
  const [roleUsers, setRoleUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [accessRoles, setAccessRoles] = useState([]);
  const [tab, setTab] = useState("access");
  const [search, setSearch] = useState("");

  const canView = user?.role === "super_admin" || user?.role === "workspace_admin";
  const isSuper = user?.role === "super_admin";

  async function load() {
    try {
      setLoading(true);
      const [s, c, r, d, ar] = await Promise.all([
        api.get("/access-management/summary"),
        api.get("/access-management/client-users"),
        api.get("/access-management/role-users"),
        api.get("/access-management/dashboards"),
        api.get("/access-management/roles"),
      ]);
      setAccessRoles(ar.data.roles || []);

      setSummary(s.data || {});
      setClientUsers(c.data.users || []);
      setRoleUsers(r.data.users || []);
      setDashboards(d.data.dashboards || []);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canView) load();
  }, [canView]);

  const filteredClients = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return clientUsers;
    return clientUsers.filter((u) =>
      [u.email, u.name, u.assigned_dashboard_id, (u.allowed_consignee_ids || []).join(",")]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [clientUsers, search]);

  const filteredRoles = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return roleUsers;
    return roleUsers.filter((u) =>
      [u.email, u.name, u.role, u.assigned_dashboard_id]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [roleUsers, search]);

  if (!canView) {
    return (
      <div className="erp-home-shell">
        <ERPAnalyticsHeader />
        <main className="erp-content">
          <div className="erp-page-title-row">
            <div>
              <h1>Access Management</h1>
              <p>Admin-only client and role access control.</p>
            </div>
          </div>
          <div className="erp-card p-6">Access denied.</div>
        </main>
      </div>
    );
  }

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content space-y-5">
        <div className="erp-page-title-row">
          <div>
            <h1>Access Management</h1>
            <p>Manage client dashboards, role-based access, data filters and feature restrictions.</p>
          </div>

          <button className="btn btn-outline" onClick={load} disabled={loading}>
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Client Users" value={summary.client_users} icon={Users} />
        <StatCard label="Client Dashboards" value={summary.client_dashboards} icon={LayoutDashboard} />
        <StatCard label="Role Dashboards" value={summary.role_dashboards} icon={Shield} />
        <StatCard label="Restricted Users" value={summary.locked_or_restricted_users} icon={Lock} />
      </div>


      <div className="card-flat p-4" style={CARD_STYLE}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex gap-2">
            <button className={`btn ${tab === "access" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("access")}>
              Roles &amp; Screens
            </button>
            <button className={`btn ${tab === "clients" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("clients")}>
              Client Users
            </button>
            <button className={`btn ${tab === "roles" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("roles")}>
              Role Users
            </button>
            <button className={`btn ${tab === "dashboards" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("dashboards")}>
              Dashboards
            </button>
            {isSuper && (
              <button className={`btn ${tab === "deleted" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("deleted")}>
                Deleted Roles
              </button>
            )}
          </div>

          {tab !== "access" && tab !== "deleted" && <input
            className="input md:w-[320px]"
            placeholder="Search users, dashboards, consignee IDs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />}
        </div>

        {tab === "access" && <RoleAccessMatrix canEdit={isSuper} onRolesChanged={setAccessRoles} />}
        {tab === "deleted" && isSuper && <DeletedRoles onRestored={load} />}

        {loading ? (
          <div className="py-10 text-center text-[13px]" style={{ color: "var(--c-text-muted)" }}>Loading access data…</div>
        ) : null}

        {tab === "clients" && <div className="mb-4"><CreateClientPanel dashboards={dashboards} onCreated={load} /></div>}
        {tab === "roles" && isSuper && <div className="mb-4"><CreateRoleUserPanel accessRoles={accessRoles} onCreated={load} /></div>}

        {!loading && tab === "clients" && (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Client User</th>
                  <th>Assigned Dashboard</th>
                  <th>Consignee IDs</th>
                  <th>Feature Access</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((u) => (
                  <ClientUserRow key={u.id} user={u} dashboards={dashboards} onSaved={load} />
                ))}
                {filteredClients.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8">No client users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tab === "roles" && (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>User</th>
                  <th>System Role</th>
                  <th>Access Role (ERP)</th>
                  <th>Assigned Dashboard</th>
                  <th>Feature Access</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((u) => (
                  <RoleUserRow key={u.id} user={u} dashboards={dashboards} accessRoles={accessRoles} canAssignRole={isSuper} onSaved={load} />
                ))}
                {filteredRoles.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8">No role users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tab === "dashboards" && (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Dashboard</th>
                  <th>ID</th>
                  <th>Tags</th>
                  <th>Allowed Roles</th>
                </tr>
              </thead>
              <tbody>
                {dashboards
                  .filter((d) => {
                    const q = search.toLowerCase().trim();
                    if (!q) return true;
                    return [d.id, d.name, (d.tags || []).join(","), (d.allowed_roles || []).join(",")]
                      .join(" ")
                      .toLowerCase()
                      .includes(q);
                  })
                  .map((d) => (
                    <tr key={d.id}>
                      <td>
                        <div className="font-medium">{d.name}</div>
                        <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{d.description}</div>
                      </td>
                      <td className="text-[12px]">{d.id}</td>
                      <td className="text-[12px]">{(d.tags || []).join(", ") || "—"}</td>
                      <td className="text-[12px]">{(d.allowed_roles || []).join(", ") || "—"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </main>
    </div>
  );
}
